/**
 * FeedbackHelpPanel — Introductory help content for the feedback widget.
 *
 * Displayed when a session starts and no comments have been recorded yet.
 * Explains how to use the tool, what we do with the information, and
 * clarifies the feedback categories available.
 *
 * Includes a "Don't show this again" checkbox to persist dismissal.
 */

import { useState } from "react";
import {
  FEEDBACK_CATEGORY_LABELS,
  type FeedbackCategory,
} from "../../types/feedback";

const CATEGORY_ICONS: Record<FeedbackCategory, string> = {
  bug: "🐛",
  kudos: "⭐",
  "feature-request": "💡",
  "docs-bug": "📄",
  usability: "🎯",
  performance: "⚡",
};

interface FeedbackHelpPanelProps {
  readonly onDismiss: (dontShowAgain: boolean) => void;
}

export function FeedbackHelpPanel({ onDismiss }: FeedbackHelpPanelProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  return (
    <div className="fb-help-panel" id="feedback-help-panel">
      <h3 className="fb-help-title">How to Share Feedback</h3>

      <p className="fb-help-intro">
        Your feedback is incredibly valuable and directly shapes how we improve
        RetireEZ. Here&rsquo;s how it works:
      </p>

      <ol className="fb-help-steps">
        <li>
          <strong>Record your voice</strong> using the microphone button, or{" "}
          <strong>type a comment</strong> in the text box below.
        </li>
        <li>
          <strong>Add multiple comments</strong> — each one captures the current
          screen automatically.
        </li>
        <li>
          <strong>Click &ldquo;Prepare Feedback&rdquo;</strong> when
          you&rsquo;re done. We&rsquo;ll organize your feedback into clear
          action items.
        </li>
        <li>
          <strong>Review and submit</strong> — edit anything before it&rsquo;s
          sent to our team.
        </li>
      </ol>

      <div className="fb-help-section">
        <h4 className="fb-help-subtitle">What Can You Share?</h4>
        <p className="fb-help-text">
          We welcome all types of feedback — not just bug reports!
        </p>
        <ul className="fb-help-categories" aria-label="Feedback categories">
          {(Object.keys(CATEGORY_ICONS) as FeedbackCategory[]).map(
            (category) => (
              <li key={category} className="fb-help-category-item">
                <span className="fb-help-category-icon" aria-hidden="true">
                  {CATEGORY_ICONS[category]}
                </span>
                <span className="fb-help-category-label">
                  {FEEDBACK_CATEGORY_LABELS[category]}
                </span>
              </li>
            )
          )}
        </ul>
      </div>

      <div className="fb-help-section">
        <h4 className="fb-help-subtitle">Your Feedback Matters</h4>
        <ul className="fb-help-details">
          <li>
            Tell us how <strong>urgent</strong> you think an issue is —
            we&rsquo;ll try to prioritize accordingly.
          </li>
          <li>
            Leave <strong>positive feedback</strong> too! Knowing what works
            helps us double down on the good stuff.
          </li>
          <li>
            Let us know if we can <strong>quote you in testimonials</strong> —
            your words can help others discover RetireEZ.
          </li>
          <li>
            Suggest <strong>new features</strong> you&rsquo;d love to see — your
            ideas drive our roadmap.
          </li>
        </ul>
      </div>

      <div className="fb-help-section">
        <h4 className="fb-help-subtitle">What Happens to Your Feedback?</h4>
        <p className="fb-help-text">
          Your voice recordings are transcribed and summarized by AI. Written
          comments are summarized in the same way. The original audio, raw
          transcript, and comment text stay private — only the AI-generated
          summary and your screen context are submitted to our development team
          as structured feedback items.
        </p>
      </div>

      <label className="fb-help-dont-show" id="feedback-dont-show-label">
        <input
          type="checkbox"
          checked={dontShowAgain}
          onChange={(e) => setDontShowAgain(e.target.checked)}
          aria-label="Don't show this again"
        />
        <span>Don&rsquo;t show this again</span>
      </label>

      <button
        type="button"
        className="fb-help-dismiss"
        onClick={() => onDismiss(dontShowAgain)}
      >
        Got It — Start Adding Feedback
      </button>
    </div>
  );
}
